import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams, formatDate } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import PersonCell from '../../components/ui/PersonCell';

const Container = styled.div``;

export default function AccessPermissionsPage() {
  const { t, i18n } = useTranslation();
  const { canWrite } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [deleting, setDeleting] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [gates, setGates] = useState([]);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    api.get('/v1/gates?pageSize=500').then((res) => {
      setGates(res.data?.data?.data || res.data?.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/access-permissions?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const handleNewButtonClick = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async (formData) => {
    try {
      const employeeIds = Array.isArray(formData.employeeIds) ? formData.employeeIds : [];
      await api.post('/v1/access-permissions/assign', {
        employeeIds,
        gateId: formData.gateId,
        validFrom: formData.validFrom,
        validTo: formData.validTo,
      });
      setRefreshTrigger((p) => p + 1);
      setDrawerOpen(false);
      return true;
    } catch {
      return false;
    }
  }, []);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: row.userFullName || '' });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: `${ids.length} ${t('accessPermission.title').toLowerCase()}` });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    try {
      if (deleteConfirm.ids.length === 1) {
        await api.delete(`/v1/access-permissions/${deleteConfirm.ids[0]}`);
      } else {
        await api.post('/v1/access-permissions/batch-revoke', { ids: deleteConfirm.ids });
      }
      setRefreshTrigger((p) => p + 1);
    } catch { /* interceptor */ }
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setDeleting(false);
  }, [deleteConfirm.ids]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, []);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const baseColumns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'userFullName', label: t('accessPermission.userFullName'), options: {
      customBodyRender: (v, tableMeta) => (
        <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.userProfilePicture} />
      ), filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'gateName', label: t('accessPermission.gateName'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'gateGroupName', label: t('accessPermission.gateGroupName'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'validFrom', label: t('accessPermission.validFrom'), options: { filter: false, sort: true, customBodyRender: (v) => formatDate(v, i18n.language) } },
    { name: 'validTo', label: t('accessPermission.validTo'), options: { filter: false, sort: true, customBodyRender: (v) => formatDate(v, i18n.language) } },
  ], [t, i18n.language, data]);

  const searchEmployees = useCallback(async (q) => {
    const params = q ? { 'firstName[like]': q, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
    const result = res.data?.data;
    const items = Array.isArray(result) ? result : (result?.data || []);
    return items.map((e) => ({
      id: e.id,
      name: `${e.firstName || ''} ${e.lastName || ''}`.trim(),
      profilePicture: e.profilePicture,
    }));
  }, []);

  const objectSchema = useMemo(() => [
    {
      name: 'employeeIds', label: t('accessPermission.userFullName'), type: 'asyncSelect', grid: 12, required: true,
      onSearch: searchEmployees, isMulti: true,
      renderOption: (o) => <PersonCell name={o.name} profilePicture={o.profilePicture} />,
    },
    {
      name: 'gateId', label: t('accessPermission.gateName'), type: 'select', grid: 12, required: false,
      options: gates, idKey: 'id', labelKey: 'name',
    },
    { name: 'validFrom', label: t('accessPermission.validFrom'), type: 'date', grid: 6 },
    { name: 'validTo', label: t('accessPermission.validTo'), type: 'date', grid: 6 },
  ], [t, gates, searchEmployees]);

  const actionOptions = useMemo(() => {
    if (!canWrite('AC')) return [];
    return [
      { type: 'delete', label: t('accessPermission.revoke'), onClick: handleDelete },
    ];
  }, [t, canWrite, handleDelete]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t('accessPermission.assign')}
        schema={objectSchema}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('accessPermission.revoke')}
        message={t('accessPermission.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      {useMemo(() => (
        <DataTable
          title={t('accessPermission.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={canWrite('AC')}
          onNewButtonClick={handleNewButtonClick}
          onRowsDelete={canWrite('AC') ? handleRowsDelete : undefined}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination, canWrite,
        handleNewButtonClick, handleRowsDelete, filterList,
        handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
